import BarScore from '@/components/BarScore/BarScore'
import BarSet from '@/components/BarSet/BarSet'
import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import './VolleyballBar.css'

import type { JsonMatchData, VolleyballScore } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface VolleyballBarProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
}

const VolleyballBar: FC<VolleyballBarProps> = ({ jsonData, isVisible = true }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Volleyball-specific score data
  const homeScore = homeCompetitor?.score as VolleyballScore | undefined
  const awayScore = awayCompetitor?.score as VolleyballScore | undefined

  const homeSetCount = homeScore?.sets ?? 0
  const awaySetCount = awayScore?.sets ?? 0
  const homePoints = homeScore?.points ?? 0
  const awayPoints = awayScore?.points ?? 0
  const homeIsServing = homeScore?.isServing ?? false
  const awayIsServing = awayScore?.isServing ?? false

  return (
    <div ref={animationRef} className="volleyball-bar-content-wrapper">
      <div
        className="volleyball-bar-card"
        style={{
          width: resolution.height,
        }}
        data-scorebar-panel
      >
        {/* FrogBox branding logo */}
        <Trapezoid
          style={{
            background: hexToRgba(theme.colors.primary, 0.95),
            width: Math.round(resolution.height * 0.3),
          }}
        >
          <img src="/assets/frogbox-logo-mono.png" alt="FrogBox" style={{ height: `calc(${theme.baseHeight} * 0.5` }} />
        </Trapezoid>

        <div className="volleyball-bar-card-content">
          <div
            className="volleyball-bar-main"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
              minHeight: `calc(${theme.baseHeight} * 2.25)`,
              paddingTop: `calc(${theme.baseHeight} * 0.1)`,
            }}
          >
            <Team data={homeCompetitor} style={{ width: '34%' }} isHome />
            <div className="volleyball-bar-scores-container">
              <BarSet value={homeSetCount} type="sets" style={{ width: '22%' }} />
              <BarScore
                homeScore={homePoints}
                homeControl={homeIsServing}
                awayScore={awayPoints}
                awayControl={awayIsServing}
                style={{ width: '56%' }}
              />
              <BarSet value={awaySetCount} type="sets" style={{ width: '22%' }} />
            </div>
            <Team data={awayCompetitor} style={{ width: '34%' }} align="right" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default VolleyballBar
